import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import AppRoutes from './components/routes';
import './pageStyles';
import Footer from './components/Footer';
import BreadcrumbSchema from './components/BreadcrumbSchema';
import { captureTrackingParams } from './utils/tracking';

import 'bootstrap/dist/css/bootstrap.min.css';
// Only the Bootstrap JS plugins the markup uses (data-bs-toggle="collapse" /
// "dropdown", data-bs-ride="carousel"); Offcanvas/Modal come from react-bootstrap.
import 'bootstrap/js/dist/collapse';
import 'bootstrap/js/dist/carousel';
import 'bootstrap/js/dist/dropdown';

// The consultation widget pulls in react-select, reCAPTCHA and the Offcanvas,
// none of which are needed for first paint, so it is loaded once the browser is idle.
const FreeConsultationWidget = lazy(() => import('./components/FreeConsultationWidget'));

const useIdle = (timeout = 3000) => {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    const done = () => setIdle(true);
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(done, { timeout });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(done, timeout);
    return () => clearTimeout(id);
  }, [timeout]);
  return idle;
};

// Reserves the viewport while a route's code chunk is loading, so the footer
// doesn't flash in at the top and then get pushed down (layout shift).
const PageFallback = () => <div style={{ minHeight: '100vh' }} aria-busy="true" />;

// Standalone landing pages render their own header/footer and skip the global site chrome.
const STANDALONE_ROUTES = [
  '/digital-marketing-lp2026',
  '/thankyou-digital-marketing-lp2026',
  '/american-independence-campaign-2026',
  '/thankyou-250-independence-campaign',
];

const AppContent = () => {
  const { pathname, search, hash } = useLocation();
  const isStandalone = STANDALONE_ROUTES.includes(pathname);
  // Not rendered into the build-time HTML snapshots (scripts/prerender.js).
  const idle = useIdle() && !window.__PRERENDER__;

  // Start every new page (e.g. /Thankyou after a form submit) at the top,
  // unless the link targets an in-page #anchor. Skipped on the first render: the
  // prerendered page may already have been scrolled before the app took over.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Capture campaign/referral/employee-referral/UTM params on every navigation
  // so attribution survives as the visitor moves between pages.
  useEffect(() => {
    captureTrackingParams(search);
  }, [search]);

  return (
    <>
      {!isStandalone && <Navbar />}
      <BreadcrumbSchema />
      <Suspense fallback={<PageFallback />}>
        <AppRoutes />
        {!isStandalone && <Footer />}
      </Suspense>
      {!isStandalone && idle && (
        <Suspense fallback={null}>
          <FreeConsultationWidget />
        </Suspense>
      )}
    </>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
